/* eslint-disable react/display-name */
import { DefaultEffects, getTheme, Icon, IconButton, Persona, PersonaSize, Stack, Text, TooltipHost } from '@fluentui/react';
import Badge from 'components/Badge';
import HoverCardHost from 'components/HoverCardHost/HoverCardHost';
import { IC } from 'components/SVGIcon';
import { BusyStatus, HandWave, Memo } from 'kits/IconKit2';
import { Building2, House, LucideIcon, Mail, MapPin, MessageCircleMore, Phone } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useControlNameWidthInMemo, useSideBarDraggableWidth, useWindowWidth } from 'shared-state/display-options/hooks';
import IvicosStrings from 'kits/language/stringKit';
import { AWAY_EXTENSION_TRIGGER_MS } from 'shared-state/identity/constants/activityTimerConstants';
import { clearIdleChainStage } from 'features/InactivityRules/inactivityStorage';
import { berlinDateString } from 'kits/berlinDateKit';
import {
    useShowFocusExtendEndModal,
    useShowOnCallEndedModal,
    useShowAwayExtensionModal,
    useAwaySinceTimestamp,
    useAtWorkLocation,
    useFocusTimeKnockDoor,
    useFocusTimeTextMessage,
    useOutOfOfficeReturnDate,
    useOutOfOfficeReturnTime,
    useOutOfOfficeAbsenceComment,
    useIdentityClaims
} from 'shared-state/identity/hooks';
import { persistOooTodayAndRedirectToLobby } from 'features/Rooms/RoomList/helpers/oooTodayLobbyRedirect';
import { getOutOfOfficePhrase } from 'features/Rooms/RoomList/helpers/oooPhrase';
import { IUserAction, IStatusAction } from '../helpers/userHooks/userTypes';
import StatusActionsHoverCard from './UserSelfStatus/StatusActionsHoverCard';
import FocusExtendEndModal from './UserSelfStatus/FocusExtendEndModal';
import FocusTimeModal from './UserSelfStatus/FocusTimeModal';
import OnCallEndedModal from './UserSelfStatus/OnCallEndedModal';
import AwayExtensionModal from './UserSelfStatus/AwayExtensionModal';
import type { IOutOfOfficePreferredMethods } from 'shared-state/identity/atoms';
import type { AtWorkLocation, UserStatus } from 'shared-state/identity/types';
import { STATUS_COLORS } from 'shared-state/identity/types';
import { IStoredStatus } from 'shared-state/identity/statusPersistence';

const AT_WORK_PHRASE_ICONS: Record<Exclude<AtWorkLocation, 'custom'>, LucideIcon> = {
    office: Building2,
    remote: House,
    other_location: MapPin
};

function formatCountdown(ms: number, durationMinutes?: number): string {
    if (ms <= 0) return '0:00';
    const remainingSeconds = Math.ceil(ms / 1000);
    const fullSeconds = durationMinutes != null ? durationMinutes * 60 : remainingSeconds;
    // Clamp to full duration when within first second so timer always starts at full value (e.g. 30:00)
    const displaySeconds =
        durationMinutes != null && remainingSeconds >= fullSeconds - 1 ? Math.min(remainingSeconds, fullSeconds) : Math.max(0, remainingSeconds);
    const m = Math.floor(displaySeconds / 60);
    const s = displaySeconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

function formatEndTime(timestamp: number): string {
    const d = new Date(timestamp);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function getStatusPhrase(status: UserStatus): string {
    switch (status) {
        case 'available':
            return IvicosStrings.statusAtWork;
        case 'busy':
            return IvicosStrings.statusDoNotDisturb;
        case 'out-of-office':
            return IvicosStrings.statusOutOfOffice;
        case 'on-the-phone':
            return IvicosStrings.statusOnThePhone;
        default:
            return IvicosStrings.statusAway;
    }
}

export interface IUserFaceActionStyles {
    hidden?: boolean;
    expanded?: boolean;
}

export interface IUserFaceViewProps {
    user: any;
    index: number;
    showDetails?: boolean;
    actionStyles?: IUserFaceActionStyles;
    profileImageURL?: string;
    actionsShouldBeDisplayOnHover: boolean;
    detailsAreHidden: boolean;
    actions: IUserAction[];
    statusActions: IStatusAction[];
    isCurrentUser: boolean;
    canUseStatus: boolean;
    currentStatusColor: string | undefined;
    awayStatusMessage: string | undefined;
    atWorkLocation: AtWorkLocation;
    atWorkCustomPhrase: string | undefined;
    currentStatus: string | undefined;
    focusEndTime: number | undefined;
    setFocusEndTime: (timestamp: number | undefined) => void;
    focusDurationMinutes: number | undefined;
    setFocusDurationMinutes: (minutes: number | undefined) => void;
    setCurrentStatus: (status: UserStatus) => void;
    outOfOfficeReturnDate: Date | undefined;
    outOfOfficeReturnTime?: string | null | undefined; // nullable for set-date out-of-office
    outOfOfficeAbsenceComment: string;
    outOfOfficePreferredMethods: IOutOfOfficePreferredMethods;
    otherUsersStatus: IStoredStatus | null;
    isUserAloneInRoom: boolean;
    isUserInMeetingInPersonalRoom: boolean;
    isUserInMeetingInPublicRoom: boolean;
}

const UserFaceView: React.FC<IUserFaceViewProps> = ({
    user,
    index,
    showDetails,
    actionStyles,
    profileImageURL,
    actionsShouldBeDisplayOnHover,
    detailsAreHidden,
    actions,
    statusActions,
    isCurrentUser,
    canUseStatus,
    currentStatusColor,
    awayStatusMessage,
    atWorkLocation,
    atWorkCustomPhrase,
    currentStatus,
    focusEndTime,
    setFocusEndTime,
    focusDurationMinutes,
    setFocusDurationMinutes,
    setCurrentStatus,
    outOfOfficeReturnDate,
    outOfOfficeReturnTime,
    outOfOfficeAbsenceComment,
    outOfOfficePreferredMethods,
    otherUsersStatus,
    isUserAloneInRoom,
    isUserInMeetingInPersonalRoom,
    isUserInMeetingInPublicRoom
}) => {
    const theme = getTheme();
    const [isHovered, setIsHovered] = useState(false);
    const [, setTick] = useState(0);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [otherUsersTick, setOtherUsersTick] = useState(0);
    const [showFocusExtendEndModal, setShowFocusExtendEndModal] = useShowFocusExtendEndModal();
    const [showOnCallEndedModal, setShowOnCallEndedModal] = useShowOnCallEndedModal();
    const [showAwayExtensionModal, setShowAwayExtensionModal] = useShowAwayExtensionModal();
    const [awaySinceTimestamp, setAwaySinceTimestamp] = useAwaySinceTimestamp();
    const [, setAtWorkLocation] = useAtWorkLocation();
    const [, setOutOfOfficeReturnDate] = useOutOfOfficeReturnDate();
    const [, setOutOfOfficeReturnTime] = useOutOfOfficeReturnTime();
    const [, setOutOfOfficeAbsenceComment] = useOutOfOfficeAbsenceComment();
    const [identityClaims] = useIdentityClaims();
    const [focusTimeKnockDoor, setFocusTimeKnockDoor] = useFocusTimeKnockDoor();
    const [focusTimeTextMessage, setFocusTimeTextMessage] = useFocusTimeTextMessage();
    const [showFocusTimeModalFromExtend, setShowFocusTimeModalFromExtend] = useState(false);

    // Clear focus end time and duration when user switches status away from busy or on-the-phone (manual status change)
    const isTimerStatus = currentStatus === 'busy' || currentStatus === 'on-the-phone';
    React.useEffect(() => {
        if (!isTimerStatus && focusEndTime) {
            setFocusEndTime(undefined);
            setFocusDurationMinutes(undefined);
        }
    }, [isTimerStatus, focusEndTime, setFocusEndTime, setFocusDurationMinutes]);

    // When busy or on-the-phone with focus end time: tick every second; on expiry show appropriate modal
    React.useEffect(() => {
        if (!isTimerStatus || !focusEndTime) return;
        if (Date.now() >= focusEndTime) {
            setFocusEndTime(undefined);
            setFocusDurationMinutes(undefined);
            if (currentStatus === 'on-the-phone') setShowOnCallEndedModal(true);
            else setShowFocusExtendEndModal(true);
            return;
        }
        const id = setInterval(() => {
            setTick((t) => t + 1);
            if (Date.now() >= focusEndTime) {
                setFocusEndTime(undefined);
                setFocusDurationMinutes(undefined);
                if (currentStatus === 'on-the-phone') setShowOnCallEndedModal(true);
                else setShowFocusExtendEndModal(true);
            }
        }, 1000);
        return () => clearInterval(id);
    }, [isTimerStatus, focusEndTime, currentStatus, setFocusEndTime, setFocusDurationMinutes, setShowFocusExtendEndModal, setShowOnCallEndedModal]);

    // Away extension: after 20 min on away (when isUserSelectedAwayManually), show modal
    React.useEffect(() => {
        if (!isCurrentUser || !canUseStatus || currentStatus !== 'away') return;
        if (localStorage.getItem('isUserSelectedAwayManually') !== 'true') return;
        if (showAwayExtensionModal) return; // Already showing modal, don't re-trigger

        const ts = awaySinceTimestamp ?? Date.now();
        if (awaySinceTimestamp == null) {
            setAwaySinceTimestamp(ts);
        }
        if (Date.now() - ts >= AWAY_EXTENSION_TRIGGER_MS) {
            setShowAwayExtensionModal(true);
            setAwaySinceTimestamp(undefined);
            return;
        }
        const id = setInterval(() => {
            const elapsed = Date.now() - (awaySinceTimestamp ?? ts);
            if (elapsed >= AWAY_EXTENSION_TRIGGER_MS) {
                setShowAwayExtensionModal(true);
                setAwaySinceTimestamp(undefined);
            }
        }, 1000);
        return () => clearInterval(id);
    }, [isCurrentUser, canUseStatus, currentStatus, showAwayExtensionModal, awaySinceTimestamp, setAwaySinceTimestamp, setShowAwayExtensionModal]);

    // Tick for other users' timers: keep them synchronized
    React.useEffect(() => {
        if (!otherUsersStatus || !otherUsersStatus.endTime) return;
        const id = setInterval(() => {
            setOtherUsersTick((t) => t + 1);
        }, 1000);
        return () => clearInterval(id);
    }, [otherUsersStatus]);

    const handleFocusExtendEndExtend = () => {
        setShowFocusExtendEndModal(false);
        setShowFocusTimeModalFromExtend(true);
    };

    const handleFocusExtendEndEnd = () => {
        setCurrentStatus('available');
        setFocusEndTime(undefined);
        setFocusDurationMinutes(undefined);
        setShowFocusExtendEndModal(false);
    };

    const handleFocusTimeFromExtendDone = (_endTimeTimestamp: number, durationMinutes: number, knockDoor: boolean, textMessage: boolean) => {
        setCurrentStatus('busy');
        setFocusDurationMinutes(durationMinutes);
        setFocusEndTime(Date.now() + durationMinutes * 60 * 1000);
        setFocusTimeKnockDoor(knockDoor);
        setFocusTimeTextMessage(textMessage);
        setShowFocusTimeModalFromExtend(false);
    };

    const handleOnCallEndedNo = useCallback(() => {
        setCurrentStatus('available');
        setFocusEndTime(undefined);
        setFocusDurationMinutes(undefined);
        setShowOnCallEndedModal(false);
    }, [setCurrentStatus, setFocusEndTime, setFocusDurationMinutes, setShowOnCallEndedModal]);

    const handleOnCallEndedYes = useCallback(
        (durationMinutes: number) => {
            setFocusDurationMinutes(durationMinutes);
            setFocusEndTime(Date.now() + durationMinutes * 60 * 1000);
            setShowOnCallEndedModal(false);
        },
        [setFocusEndTime, setFocusDurationMinutes, setShowOnCallEndedModal]
    );

    const handleAwayExtensionYes = useCallback(() => {
        setCurrentStatus('available');
        setAtWorkLocation('office');
        localStorage.removeItem('isUserSelectedAwayManually');
        clearIdleChainStage();
        setAwaySinceTimestamp(undefined);
        setShowAwayExtensionModal(false);
    }, [setCurrentStatus, setAtWorkLocation, setAwaySinceTimestamp, setShowAwayExtensionModal]);

    const handleAwayExtensionNo = useCallback(() => {
        setAwaySinceTimestamp(Date.now());
        setShowAwayExtensionModal(false);
    }, [setAwaySinceTimestamp, setShowAwayExtensionModal]);

    const handleAwayExtensionTimeout = useCallback(() => {
        const userId = identityClaims?.uid;
        if (!userId) return;

        const todayStr = berlinDateString();
        localStorage.removeItem('isUserSelectedOooManually');
        clearIdleChainStage();
        setCurrentStatus('out-of-office');
        setOutOfOfficeReturnDate(new Date(`${todayStr}T00:00:00Z`));
        setOutOfOfficeReturnTime(null);
        setOutOfOfficeAbsenceComment('');
        void persistOooTodayAndRedirectToLobby(userId, todayStr, 'status-updated');
    }, [identityClaims?.uid, setCurrentStatus, setOutOfOfficeReturnDate, setOutOfOfficeReturnTime, setOutOfOfficeAbsenceComment]);

    // Close hover card when status is selected (ring appears)
    React.useEffect(() => {
        if (currentStatusColor) {
            setIsHovered(false);
        }
    }, [currentStatusColor]);

    const renderActions = () =>
        actions?.map((action) => (
            <TooltipHost key={action.key} content={smallDevice ? '' : action.title}>
                <IconButton
                    onClick={() => action.action()}
                    onRenderIcon={() => (
                        <IC variant="dark" size={16}>
                            {action.onRenderIcon && action.onRenderIcon()}
                        </IC>
                    )}
                />
            </TooltipHost>
        ));

    const { smallDevice } = useWindowWidth();
    const [sideBarDraggableWidth] = useSideBarDraggableWidth();
    const decreasedSideBarDraggableWidth = sideBarDraggableWidth - 160;
    const decreasedWindowInnerWidth = window.innerWidth - 175;

    const [controlNameWidthInMemo] = useControlNameWidthInMemo();

    const stylesForUserPersona = {
        primaryText: {
            maxWidth: controlNameWidthInMemo ? 320 : smallDevice ? decreasedWindowInnerWidth : sideBarDraggableWidth ? decreasedSideBarDraggableWidth : 160
        }
    };

    // For current user: render inline hover with actions on the right of the image
    // Also display a colored ring around the profile based on selected status
    if (isCurrentUser && canUseStatus && user.type === 'user') {
        const statusPhrase =
            currentStatus === 'busy' && focusEndTime && focusEndTime > Date.now()
                ? IvicosStrings.focusWorkUntilEnd + ' ' + formatEndTime(focusEndTime)
                : currentStatus === 'on-the-phone' && focusEndTime && focusEndTime > Date.now()
                ? IvicosStrings.onCallUntilEnd + ' ' + formatEndTime(focusEndTime)
                : currentStatus === 'away'
                ? awayStatusMessage
                : currentStatus === 'out-of-office'
                ? getOutOfOfficePhrase(outOfOfficeAbsenceComment, outOfOfficeReturnDate, outOfOfficeReturnTime) ?? getStatusPhrase(currentStatus as UserStatus)
                : currentStatus === 'available' && atWorkLocation === 'custom' && atWorkCustomPhrase?.trim()
                ? atWorkCustomPhrase.trim()
                : currentStatus
                ? getStatusPhrase(currentStatus as UserStatus)
                : undefined;

        return (
            <>
                <Stack
                    horizontal
                    verticalAlign="center"
                    style={{ position: 'relative', overflow: 'visible' }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {/* Profile Picture (with optional status ring after user selects one) */}
                    <Stack
                        style={{
                            position: 'relative',
                            cursor: 'pointer',
                            zIndex: isHovered ? 1000 : 1
                        }}
                    >
                        {currentStatusColor ? (
                            <>
                                {/* Status ring container - only when a status is selected */}
                                <Stack
                                    style={{
                                        position: 'relative',
                                        padding: 3,
                                        borderRadius: '50%',
                                        background: isUserInMeetingInPersonalRoom
                                            ? '#2099df'
                                            : isUserInMeetingInPublicRoom
                                            ? '#145f9f'
                                            : `linear-gradient(135deg, ${currentStatusColor}, ${currentStatusColor}dd)`
                                    }}
                                >
                                    <Stack
                                        style={{
                                            padding: 2,
                                            borderRadius: '50%',
                                            background: theme.palette.white
                                        }}
                                    >
                                        <Persona
                                            styles={{ ...stylesForUserPersona, root: { margin: 0 } }}
                                            size={PersonaSize.size40}
                                            text={user.displayName}
                                            imageUrl={profileImageURL}
                                            hidePersonaDetails={true}
                                            aria-label="user-name-in-user-face"
                                        />
                                    </Stack>
                                </Stack>
                                {/* Timer badge (busy or on-the-phone): countdown on profile pic */}
                                {(currentStatus === 'busy' || currentStatus === 'on-the-phone') &&
                                    focusEndTime &&
                                    focusEndTime > Date.now() &&
                                    isUserAloneInRoom && (
                                        <Stack
                                            horizontal
                                            verticalAlign="center"
                                            tokens={{ childrenGap: 4 }}
                                            style={{
                                                position: 'absolute',
                                                top: -6,
                                                left: -4,
                                                zIndex: 10,
                                                padding: '4px',
                                                borderRadius: 20,
                                                backgroundColor: theme.palette.white,
                                                boxShadow: DefaultEffects.elevation4
                                            }}
                                        >
                                            {/* <Icon
                                                iconName={currentStatus === 'on-the-phone' ? 'Phone' : 'Clock'}
                                                style={{ color: currentStatus === 'busy' ? STATUS_COLORS.busy : STATUS_COLORS['on-the-phone'], fontSize: 14 }}
                                            /> */}
                                            {currentStatus === 'on-the-phone' ? (
                                                <Icon iconName="Phone" style={{ color: STATUS_COLORS['on-the-phone'], fontSize: 14 }} />
                                            ) : (
                                                <IC variant="custom" size={12} iconColor={STATUS_COLORS.busy}>
                                                    <BusyStatus />
                                                </IC>
                                            )}
                                            <Text variant="small" style={{ fontWeight: 600, color: theme.palette.black }}>
                                                {formatCountdown(focusEndTime - Date.now(), focusDurationMinutes)}
                                            </Text>
                                        </Stack>
                                    )}
                            </>
                        ) : (
                            <Persona
                                styles={stylesForUserPersona}
                                size={PersonaSize.size40}
                                text={user.displayName}
                                imageUrl={profileImageURL}
                                hidePersonaDetails={true}
                                aria-label="user-name-in-user-face"
                            />
                        )}
                    </Stack>

                    {/* User name displayed next to the profile */}
                    {showDetails && (
                        <Stack verticalAlign="center" tokens={{ childrenGap: 2 }} style={{ marginLeft: 8 }}>
                            <Text
                                variant="medium"
                                style={{
                                    color: theme.palette.black,
                                    maxWidth: stylesForUserPersona.primaryText.maxWidth,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {user.displayName}
                            </Text>
                            {/* Status phrase displayed below user name for all statuses */}
                            {statusPhrase && isUserAloneInRoom && (
                                <TooltipHost content={statusPhrase}>
                                    <Stack
                                        horizontal
                                        verticalAlign="center"
                                        tokens={{ childrenGap: 4 }}
                                        style={{
                                            maxWidth: stylesForUserPersona.primaryText.maxWidth,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {currentStatus === 'available' &&
                                            atWorkLocation !== 'custom' &&
                                            (() => {
                                                const LocationIcon = AT_WORK_PHRASE_ICONS[atWorkLocation];
                                                return <LocationIcon size={14} color={theme.palette.neutralSecondary} />;
                                            })()}
                                        <Text
                                            variant="small"
                                            style={{
                                                color: theme.palette.neutralSecondary,
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                                fontSize: 12,
                                                fontWeight: 500
                                            }}
                                        >
                                            {statusPhrase}
                                        </Text>
                                    </Stack>
                                </TooltipHost>
                            )}
                            {currentStatus === 'busy' && focusEndTime && focusEndTime > Date.now() && (focusTimeKnockDoor || focusTimeTextMessage) && (
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} style={{ marginRight: 'auto' }}>
                                    <Text variant="small" style={{ color: theme.palette.neutralSecondary, fontSize: 12 }}>
                                        {IvicosStrings.focusTimeEmergencyContactLabel}
                                    </Text>
                                    {focusTimeKnockDoor && (
                                        <IC variant="dark" size={12}>
                                            <HandWave />
                                        </IC>
                                    )}
                                    {focusTimeTextMessage && (
                                        <IC variant="dark" size={12}>
                                            <Memo />
                                        </IC>
                                    )}
                                </Stack>
                            )}
                            {currentStatus === 'out-of-office' &&
                                (outOfOfficePreferredMethods.email ||
                                    outOfOfficePreferredMethods.chat ||
                                    outOfOfficePreferredMethods.sms ||
                                    outOfOfficePreferredMethods.call) && (
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} style={{ marginRight: 'auto' }}>
                                        <Text variant="small" style={{ color: theme.palette.neutralSecondary, fontSize: 12 }}>
                                            {IvicosStrings.focusTimeEmergencyContactLabel}
                                        </Text>
                                        {outOfOfficePreferredMethods.email && <Mail size={12} />}
                                        {outOfOfficePreferredMethods.chat && (
                                            <IC variant="dark" size={12}>
                                                <Memo />
                                            </IC>
                                        )}
                                        {outOfOfficePreferredMethods.sms && <MessageCircleMore size={12} />}
                                        {outOfOfficePreferredMethods.call && <Phone size={12} />}
                                    </Stack>
                                )}
                        </Stack>
                    )}

                    {/* Actions displayed immediately to the RIGHT of the profile image, overlaying the name */}
                    {isHovered && statusActions.length > 0 && (
                        <Stack
                            horizontal
                            verticalAlign="center"
                            style={{
                                position: 'absolute',
                                left: 52, // Position right after the ring (40px image + 6px ring padding + 6px gap)
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 1001 // Higher than everything else
                            }}
                        >
                            {isUserAloneInRoom && <StatusActionsHoverCard actions={statusActions} onCloseIcons={() => setIsHovered(false)} />}
                        </Stack>
                    )}
                </Stack>
                <FocusExtendEndModal
                    isOpen={showFocusExtendEndModal}
                    onExtend={handleFocusExtendEndExtend}
                    onEnd={handleFocusExtendEndEnd}
                    onDismiss={handleFocusExtendEndEnd}
                />
                <FocusTimeModal
                    isOpen={showFocusTimeModalFromExtend}
                    onDone={handleFocusTimeFromExtendDone}
                    onDismiss={() => setShowFocusTimeModalFromExtend(false)}
                />
                <OnCallEndedModal isOpen={showOnCallEndedModal} onNo={handleOnCallEndedNo} onYes={handleOnCallEndedYes} onDismiss={handleOnCallEndedNo} />
                <AwayExtensionModal
                    isOpen={showAwayExtensionModal}
                    onNo={handleAwayExtensionNo}
                    onYes={handleAwayExtensionYes}
                    onDismiss={handleAwayExtensionNo}
                    onTimeout={handleAwayExtensionTimeout}
                />
            </>
        );
    }

    // For other users: use existing HoverCardHost behavior
    const otherUsersStatusPhrase = otherUsersStatus
        ? otherUsersStatus.status === 'busy' && otherUsersStatus.endTime && otherUsersStatus.endTime > Date.now()
            ? IvicosStrings.focusWorkUntilEnd + ' ' + formatEndTime(otherUsersStatus.endTime)
            : otherUsersStatus.status === 'on-the-phone' && otherUsersStatus.endTime && otherUsersStatus.endTime > Date.now()
            ? IvicosStrings.onCallUntilEnd + ' ' + formatEndTime(otherUsersStatus.endTime)
            : otherUsersStatus.status === 'away'
            ? otherUsersStatus.phrase
            : otherUsersStatus.status === 'out-of-office'
            ? getOutOfOfficePhrase(
                  otherUsersStatus.absenceComment ?? '',
                  otherUsersStatus.returnDate ? new Date(otherUsersStatus.returnDate) : undefined,
                  otherUsersStatus.returnTime ?? undefined
              ) ?? getStatusPhrase(otherUsersStatus.status as UserStatus)
            : otherUsersStatus.status === 'available' && otherUsersStatus.phrase
            ? otherUsersStatus.phrase
            : otherUsersStatus.status
            ? getStatusPhrase(otherUsersStatus.status as UserStatus)
            : undefined
        : undefined;

    return (
        <Stack className="hoverable" horizontal style={{ position: 'relative', overflow: 'visible' }} verticalAlign="center" horizontalAlign="space-between">
            {(actionsShouldBeDisplayOnHover || detailsAreHidden) && (
                <Stack
                    horizontalAlign={index <= 2 ? 'start' : 'end'}
                    className="display--on-parent-hover"
                    style={{ position: 'absolute', zIndex: 300, bottom: 30, left: index > 2 ? undefined : 16, right: index <= 2 ? undefined : 16, width: 300 }}
                ></Stack>
            )}
            <HoverCardHost
                disabled={!actionsShouldBeDisplayOnHover && !detailsAreHidden}
                content={
                    <Stack
                        horizontal
                        className="padding--small "
                        verticalAlign="center"
                        tokens={{ childrenGap: 4 }}
                        style={{ minHeight: 32, borderRadius: 4, boxShadow: DefaultEffects.elevation16, background: theme.palette.white }}
                    >
                        {actionsShouldBeDisplayOnHover && (
                            <>
                                <Stack horizontal>{renderActions()}</Stack>
                                {detailsAreHidden && <div style={{ width: 1, height: 24, minHeight: 24, background: theme.palette.neutralLight }} />}
                            </>
                        )}
                        {detailsAreHidden && <Text className={'padding_horizontal--small'}>{user.displayName}</Text>}
                    </Stack>
                }
            >
                {user.type === 'user' ? (
                    <Stack>
                        {otherUsersStatus ? (
                            <>
                                <Stack
                                    style={{
                                        position: 'relative',
                                        padding: 3,
                                        borderRadius: '50%',
                                        background: isUserInMeetingInPersonalRoom
                                            ? '#2099df'
                                            : isUserInMeetingInPublicRoom
                                            ? '#145f9f'
                                            : `linear-gradient(135deg, ${STATUS_COLORS[otherUsersStatus.status as UserStatus]}, ${
                                                  STATUS_COLORS[otherUsersStatus.status as UserStatus]
                                              }dd)`
                                    }}
                                >
                                    <Stack
                                        style={{
                                            padding: 2,
                                            borderRadius: '50%',
                                            background: theme.palette.white
                                        }}
                                    >
                                        <Persona
                                            styles={{ ...stylesForUserPersona, root: { margin: 0 } }}
                                            size={PersonaSize.size40}
                                            text={user.displayName}
                                            imageUrl={profileImageURL}
                                            hidePersonaDetails={true}
                                            aria-label="user-name-in-user-face"
                                        />
                                    </Stack>
                                </Stack>
                                {/* Timer badge (busy or on-the-phone): countdown on profile pic */}
                                {/* commented this for now, to be removed later */}
                                {/* {(otherUsersStatus.status === 'busy' || otherUsersStatus.status === 'on-the-phone') &&
                                    otherUsersStatus.endTime &&
                                    otherUsersStatus.endTime > Date.now() &&
                                    isUserAloneInRoom && (
                                        <Stack
                                            horizontal
                                            verticalAlign="center"
                                            tokens={{ childrenGap: 4 }}
                                            style={{
                                                position: 'absolute',
                                                top: -6,
                                                left: -4,
                                                zIndex: 10,
                                                padding: '4px',
                                                borderRadius: 20,
                                                backgroundColor: theme.palette.white,
                                                boxShadow: DefaultEffects.elevation4
                                            }}
                                        >
                                            {otherUsersStatus.status === 'on-the-phone' ? (
                                                <Icon iconName="Phone" style={{ color: STATUS_COLORS['on-the-phone'], fontSize: 14 }} />
                                            ) : (
                                                <IC variant="custom" size={12} iconColor={STATUS_COLORS.busy}>
                                                    <BusyStatus />
                                                </IC>
                                            )}
                                            <Text variant="small" style={{ fontWeight: 600, color: theme.palette.black }}>
                                                {formatCountdown(otherUsersStatus.endTime - Date.now(), otherUsersStatus.durationMinutes ?? undefined)}
                                            </Text>
                                        </Stack>
                                    )} */}
                            </>
                        ) : (
                            <Persona
                                styles={stylesForUserPersona}
                                size={PersonaSize.size40}
                                text={user.displayName}
                                imageUrl={profileImageURL}
                                hidePersonaDetails={!showDetails}
                                aria-label="user-name-in-user-face"
                            />
                        )}
                    </Stack>
                ) : (
                    <Stack horizontal verticalAlign="center">
                        <Stack horizontalAlign="center" verticalAlign="start" style={{ width: '40px', paddingRight: 10, marginLeft: 5 }}>
                            <Badge text={user.displayName} size="51" fontSize="medium" />
                        </Stack>
                        {showDetails && (
                            <Text
                                variant="medium"
                                style={{
                                    color: theme.palette.black,
                                    marginLeft: 7,
                                    width: '70%',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {user.displayName}
                            </Text>
                        )}
                    </Stack>
                )}

                {showDetails && !actionStyles?.hidden && actionStyles?.expanded && <Stack horizontal>{renderActions()}</Stack>}
            </HoverCardHost>

            {/* Status phrase displayed next to profile for other users */}
            {showDetails && otherUsersStatus && isUserAloneInRoom && (
                <Stack verticalAlign="center" tokens={{ childrenGap: 2 }} style={{ marginLeft: 8 }}>
                    <Text
                        variant="medium"
                        style={{
                            color: theme.palette.black,
                            maxWidth: stylesForUserPersona.primaryText.maxWidth,
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {user.displayName}
                    </Text>
                    {otherUsersStatusPhrase && isUserAloneInRoom && (
                        <TooltipHost content={otherUsersStatusPhrase}>
                            <Stack
                                horizontal
                                verticalAlign="center"
                                tokens={{ childrenGap: 4 }}
                                style={{
                                    maxWidth: stylesForUserPersona.primaryText.maxWidth,
                                    overflow: 'hidden'
                                }}
                            >
                                {otherUsersStatus.status === 'available' &&
                                    otherUsersStatus.location &&
                                    otherUsersStatus.location !== 'custom' &&
                                    (() => {
                                        const LocationIcon = AT_WORK_PHRASE_ICONS[otherUsersStatus.location];
                                        return <LocationIcon size={14} color={theme.palette.neutralSecondary} />;
                                    })()}
                                <Text
                                    variant="small"
                                    style={{
                                        color: theme.palette.neutralSecondary,
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        fontSize: 12,
                                        fontWeight: 500
                                    }}
                                >
                                    {otherUsersStatusPhrase}
                                </Text>
                            </Stack>
                        </TooltipHost>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default UserFaceView;
